/**
 * The short explanations shown on hover, in one place.
 *
 * Two dictionaries rather than props on hundreds of call sites: a field's help
 * follows its name wherever that field appears, and a button's follows its
 * label. Adding a screen inherits the copy without touching it, and a term is
 * worded the same way everywhere — which is the actual value, since the same
 * `insuranceWage` means the same thing on four screens.
 *
 * Keep entries to one sentence. Anything longer belongs in the screen's own
 * explanatory text, where it is visible without hovering.
 */

/** Keyed by the field's `name`, matched exactly. */
export const FIELD_HELP: Record<string, string> = {
  flexibleHours: 'No fixed start or end: whatever the person punches that day is the shift. Late and early marking are off; overtime is what exceeds the overtime threshold.',
  enableLateEntryMarking: 'Flags an arrival after the shift start plus the grace minutes, and records how late.',
  lateEntryGracePeriodMinutes: 'Minutes after the start that do not count as late.',
  enableEarlyExitMarking: 'Flags a departure before the shift end minus the grace minutes.',
  earlyExitGracePeriodMinutes: 'Minutes before the end that do not count as early.',
  workingHoursThresholdForHalfDay: 'Fewer hours than this marks the day a half day.',
  workingHoursThresholdForAbsent: 'Fewer hours than this marks the day absent even with punches.',
  overtimeAfterHours: 'Hours worked beyond this are overtime for the day; empty uses the company working day from HR settings.',
  geofenceRadiusMeters: 'How far from the branch pin a phone check-in still counts as being at work.',
  // --- payroll settings ---
  considerUnmarkedAttendanceAs: 'A day with no attendance record is paid (Present) or unpaid (Absent). Keep Present until attendance is complete, or people with missing data get zero pay.',
  standardMonthDays: 'Days a month is worth: a day’s wage is the monthly salary divided by this. 0 uses the period’s real length.',
  includeHolidaysInTotalWorkingDays: 'Holidays count as working days, so they are paid, not deducted.',
  dailyWagesFractionForHalfDay: 'Share of a day’s wage a half day pays. Only charged once HR confirms the half day in the attendance review.',
  absentPenaltyDays: 'Days of wage proposed when HR confirms an absence in the attendance review, the absent day included.',
  missingPunchPenaltyDays: 'Days of wage proposed when HR confirms a missing punch in the attendance review.',
  latePenaltyDays: 'Days of wage proposed when HR confirms a late arrival. 0 proposes nothing.',
  earlyExitPenaltyDays: 'Days of wage proposed when HR confirms an early exit. 0 proposes nothing.',
  disableRoundedTotal: 'Leave off: net pay is rounded to the nearest pound.',
  // --- identity and naming ---
  name: 'What this record is called. Shown wherever it is referenced.',
  nameAr: 'The Arabic name, shown when the interface is in Arabic.',
  code: 'The reference this record is known by. Generated from a naming series.',
  abbr: 'Short code used where the full name will not fit. Fixed once created.',
  description: 'Free notes. Not used in any calculation.',
  label: 'How this appears on screen, as opposed to its internal name.',
  labelAr: 'The Arabic wording shown when the interface is in Arabic.',
  isActive: 'Inactive records stay in history but are no longer offered for new documents.',

  // --- people ---
  employeeId: 'Who this record is about.',
  fullName: 'The person’s full name as it should appear on documents.',
  fullNameAr: 'The Arabic full name, used on Arabic documents and payslips.',
  firstName: 'Given name, as it appears on the national ID.',
  middleName: 'Middle name, if the national ID carries one.',
  lastName: 'Family name, as it appears on the national ID.',
  employeeNumber: 'Your own payroll number for this person, separate from the system reference.',
  nationalId: 'Egyptian national ID. Checked against the birth date and gender it encodes.',
  dateOfBirth: 'Used to validate the national ID and to calculate age-based entitlements.',
  dateOfJoining: 'Start of service. Drives gratuity, leave accrual and probation.',
  gender: 'Must agree with the digit the national ID encodes.',
  cellNumber: 'Mobile number for notifications and the exit questionnaire.',
  email: 'Where correspondence for this record is sent.',
  mobile: 'Mobile number used for notifications.',

  // --- statutory ---
  insuranceStatus: 'Whether social insurance applies. An insured employee must carry an insurable wage.',
  administrationId: 'The division the department sits under — Kitchen, Restaurant, Head Office.',
  gradeId: 'Pay grade. Its band bounds the contract, and its defaults draft the salary and leave assignments on save.',
  employmentTypeId: 'Full time, part time, seasonal. Reporting groups on it; pay does not depend on it.',
  contractEndDate: 'For a fixed-term contract. Tracked documents warn before it lapses.',
  holidayListId: 'Which calendar of weekly rest days and official holidays applies. Falls back to the company default.',
  defaultShiftId: 'The shift used when no roster covers a day — attendance still needs a schedule to be generated.',
  reportsToId: 'The line manager. Approvals fall to them where no explicit approver is named.',
  leaveApproverId: 'Who approves this person’s leave. Left empty, it falls to the line manager.',
  expenseApproverId: 'Who approves this person’s expense claims. Left empty, it falls to the line manager.',
  shiftRequestApproverId: 'Who approves this person’s shift requests. Left empty, it falls to the line manager.',
  defaultSalaryStructureId: 'Drafted onto a new hire in this grade, so a contract does not have to be built by hand.',
  defaultLeavePolicyId: 'Drafted onto a new hire in this grade, so entitlements do not have to be allocated by hand.',
  attendanceMode:
    'Whether a terminal reports this person’s days, or they are present by default — for someone no reader covers.',
  logType: 'In or out. Left alone, a punch takes the opposite of the one before it.',
  timestamp: 'The moment of the punch, not just the day.',
  insuranceWage: 'The wage social insurance is calculated on, capped by the statutory band.',
  socialInsuranceNumber: 'The employee’s registration number with the insurance authority.',
  establishmentNumber: 'The employer’s establishment number the employee is registered under.',
  taxYear: 'The year this applies to. Brackets and caps are dated, so the year decides which apply.',
  maxAmount: 'The absolute annual ceiling, before any percentage cap is applied.',
  maxPercentOfIncome: 'A second cap as a share of net taxable income, applied during the payroll run.',

  // --- money ---
  amount: 'A fixed amount. Leave empty if a formula decides it instead.',
  base: 'The salary this assignment is built on. Components refer to it as `base`.',
  formula: 'An expression over the payroll variables, used instead of a fixed amount.',
  condition: 'The component is skipped entirely when this evaluates false.',
  isTaxApplicable: 'Whether this counts toward taxable income.',
  exemptedFromIncomeTax: 'Excluded from taxable income even though it is paid.',
  dependsOnPaymentDays: 'Prorated by days actually paid, rather than paid in full.',
  advanceAmount: 'What is being handed over, before anything is recovered or returned.',
  repaymentMonths: 'How many months to recover it over. Zero recovers it against the next claim.',
  currency: 'Amounts are held in this currency; reporting converts at the stored rate.',

  // --- dates ---
  fromDate: 'First day this covers, inclusive.',
  toDate: 'Last day this covers, inclusive. Leave empty for open-ended.',
  startDate: 'When this takes effect.',
  endDate: 'When this stops applying. Leave empty for open-ended.',
  effectiveFrom: 'The date the assignment starts applying. Earlier documents are unaffected.',
  effectiveTo: 'The date it stops applying.',
  postingDate: 'The date the document is booked on, which decides the period it falls in.',
  expiryDate: 'When the document lapses. Drives the compliance warnings.',
  issueDate: 'When the document was issued.',
  issuePlace: 'Where the document was issued, as printed on it.',

  // --- documents and lifecycle ---
  documentTypeId: 'Which document this is. The type decides whether it is mandatory.',
  documentNumber: 'The number printed on the document itself.',
  reason: 'Why this was raised. Kept on the record and visible to approvers.',
  remarks: 'Notes for whoever reviews this. Not used in any calculation.',
  status: 'Where this sits in its own lifecycle, separate from draft and submitted.',

  // --- time ---
  attendanceDate: 'The day being recorded.',
  halfDay: 'Only part of the period is taken.',
  halfDayDate: 'Which day within the range is the half day.',
  shiftTypeId: 'The working window this applies to, and whether checkins build attendance from it.',
  startTime: 'When the shift begins, as HH:MM:SS.',
  endTime: 'When the shift ends. Earlier than the start means it runs past midnight.',
  overtimeDate: 'The day the extra hours were worked.',
  totalHours: 'Hours claimed. The multiplier comes from the overtime type.',
  multiplier: 'What an hour of this kind is paid at, relative to the normal rate.',

  // --- leave ---
  leaveTypeId: 'Which entitlement this draws on.',
  newLeavesAllocated: 'Days added to the balance by this allocation.',
  carryForward: 'Unused days roll into the next period rather than lapsing.',
  days: 'Positive adds to the balance, negative takes days away.',
  encashableDays: 'Days being paid out instead of taken.',
  daysGranted: 'Days credited in return for the time worked.',

  // --- organisation ---
  companyId: 'The legal entity this belongs to. Scopes who can see it.',
  branchId: 'The location this belongs to. Supplies the daily meal and transport rates.',
  departmentId: 'The department this belongs to, which also decides who approves it.',
  designationId: 'The job title. Staffing plans budget by designation.',
  parentId: 'The department this sits under. Leave empty for a top-level one.',
  isGroup: 'A group can contain other departments; a leaf cannot.',
  biometricDeviceId: 'The serial the terminal reports. This is what routes a punch to this branch.',
  latitude: 'Where the branch actually is. Paste a maps link or coordinates, or stand in the branch and use the device’s location. A shortened goo.gl link holds no coordinates — open it first.',
  mealAllowancePerDay: 'Paid per attended day at this location.',
  transportAllowancePerDay: 'Paid per attended day at this location.',
  variableTransportAllowancePerDay: 'Paid per attended day to anyone whose first check-in that day is here, on top of their own transport allowance.',

  // --- access ---
  roles: 'What this user may do. Companies and row scope are set separately.',
  companies: 'Which legal entities this user may operate in.',
  password: 'At least ten characters. The user is asked to change it at next sign-in.',
  language: 'Which language this user sees the interface in.',
  deskAccess: 'Whether the role may sign in to the back office at all.',

  // --- recruitment and talent ---
  jobApplicantId: 'The candidate this concerns.',
  jobOpeningId: 'The vacancy applied for. Leave empty for a speculative application.',
  numberOfPositions: 'How many people are being hired against this.',
  goalName: 'What the person is being measured on.',
  progress: 'How far along, as a percentage. Feeds the goal part of the appraisal score.',
  selfScore: 'The employee’s own assessment, weighted by the cycle.',
  rating: 'Score given, on the scale the round expects.',

  // --- fields the capture found without copy ---
  submitNow: 'Submits as soon as it is saved. A submitted record can only be corrected by cancelling and amending.',
  type: 'Which kind this is. Decides which rules apply to it.',
  phone: 'Contact number.',
  modeOfPayment: 'How the money changed hands — bank, cash, cheque.',
  trainingEventId: 'Which event this concerns.',
  model: 'The terminal’s make and model, for the record.',
  deviceId: 'The serial the terminal reports about itself. Must match what it sends.',
  purpose: 'What this is for. Kept on the record and visible to approvers.',
  sourceId: 'Where the candidate came from. The source effectiveness report reads this.',
  location: 'Where it takes place.',
  responseDate: 'When the candidate answered.',
  trainer: 'Who delivers the event.',
  policyNumber: 'The number on the policy document.',
  subject: 'A short heading, shown in lists.',
  address: 'Postal address.',
  salaryMode: 'How salary is paid — bank transfer, cash or cheque.',
  bank: 'The bank the salary is paid into.',
  accountNumber: 'The account the salary is paid into.',
  iban: 'The account’s IBAN, used on bank remittance files.',
  mealAllowance: 'Whether this person receives the branch meal allowance.',
  fixedMealAmount: 'Overrides the branch daily meal rate for this person.',
  transportAllowance: 'Whether this person receives the branch transport allowance.',
  fixedTransportAmount: 'Overrides the branch daily transport rate for this person.',
  employeeName: 'The name shown wherever this person is referenced.',
  ownerDoctype: 'What kind of record holds this document — an employee or a vehicle.',
  ownerId: 'Which employee or vehicle this document belongs to.',
  fileId: 'The document itself — a photo, or the PDF as issued.',
  interviewRoundId: 'Which round this is. A round clears when the panel average reaches its expected rating.',
  scheduledOn: 'When the interview takes place.',
  eventName: 'What the event is called.',
  level: 'Who the event is pitched at.',
  // real column names behind labels the forms already carried
  title: 'What this is called.',
  titleAr: 'The Arabic name, shown when the interface is in Arabic.',
  templateTitle: 'What the template is called.',
  perWeightage: 'How much this criterion counts, as a share of the feedback score.',
  reviewerUserId: 'Who is giving the feedback. A user, since they sign in to give it.',
  termTemplateId: 'The named bundle of terms the offer is assembled from.',
  templateId: 'The template the letter’s body comes from.',
  referrerEmployeeId: 'The member of staff who introduced the candidate.',
  expectedBy: 'When the position needs to be filled by.',
  reasonForRequesting: 'Why the position is needed.',
  categoryId: 'Which relief category this sits under.',
  maxPercentOfNetIncome: 'A second cap as a share of net taxable income.',
  escalateToHrDirectly: 'Routes the case past the line manager, for when the manager is its subject.',
  trainerName: 'Who delivers the event.',
  school: 'Where it was obtained.',
  insuranceOffice: 'The insurance office the registration was filed at.',
  maximumAmountPerClaim: 'The most one claim line of this type can be sanctioned for.',
  requiresReceipt: 'A line of this type must carry a receipt to be sanctioned.',
  disabled: 'Switched off. Kept for history, no longer applied.',
  minimumYearsForGratuity: 'Years of service before any gratuity is due.',
  totalWorkingDaysPerYear: 'The divisor used to turn a salary into a daily rate.',
  introduction: 'Opening paragraph, before the body.',
  closingNotes: 'Closing paragraph, after the body.',
  contactPerson: 'Who to speak to there.',
  weekdays: 'Which days of the week the rota runs.',
  generateAheadDays: 'How far forward the nightly run projects assignments. Thirty days is the norm.',
  lastGeneratedUntil: 'The last day assignments have been generated for.',
  // --- every dialog field the guide found without copy ---
  explanation: 'Anything the approver should know beyond the reason.',
  color: 'Colour the shift shows in on the roster.',
  enableAutoAttendance: 'Whether the nightly pass builds attendance from this shift’s punches.',
  beginCheckinBeforeShiftStartMinutes: 'How early a punch still counts as this shift’s check-in.',
  allowCheckoutAfterShiftEndMinutes: 'How late a punch still counts as this shift’s check-out.',
  routeBranch: 'Sets this device as the branch’s terminal, so its punches carry the branch’s meal and transport rates. Off, and punches fall back to the employee’s home branch.',
  ipAddress: 'Where the terminal sits on the network. Recorded for reference; nothing connects to it.',
  employeeDeviceId: 'The badge or enrolment number the terminal reports for this person.',
  time: 'When the punch happened, as the terminal saw it.',
  amountPerDay: 'What each encashed day is paid at. Defaults to the daily rate from the structure.',
  workFromDate: 'First day of the holiday or rest-day work being claimed back.',
  workEndDate: 'Last day of it.',
  maxLeavesAllowed: 'Days of this type an employee may take in a period.',
  applicableAfterDays: 'Days of service before this type can be taken at all.',
  isLwp: 'Leave without pay — days are deducted from salary rather than from a paid balance.',
  isCarryForward: 'Unused days roll into the next period rather than lapsing.',
  allowEncashment: 'Unused days can be paid out instead of taken.',
  includeHoliday: 'A holiday inside the range counts as a day of leave.',
  requiresAttachment: 'An application must carry evidence — a medical certificate, a marriage certificate.',
  statutoryReference: 'The article of the labour law this type comes from, for the record.',
  boardingBeginsOn: 'The day the checklist starts. Activities are dated from here.',
  relievingDate: 'The employee’s last day. Separation activities are dated back from it.',
  notifyByEmail: 'Emails each activity’s owner when it becomes due.',
  changeNote: 'Optional note kept with the change, shown in the employee’s history.',
  gratuityRuleId: 'Which rule the amount is calculated under.',
  payViaSalarySlip: 'Adds the gratuity to the final payslip rather than paying it separately.',
  transactionDate: 'The date the statement is booked on.',
  isNight: 'Hours worked at night. Attracts the night multiplier.',
  isRestDay: 'Hours worked on the weekly rest day.',
  isOfficialHoliday: 'Hours worked on an official public holiday. The multiplier is a disputed reading and ships as configuration.',
  grantsCompensatoryDay: 'Working this earns a day off in lieu as well as the pay.',
  overtimeTypeId: 'Which kind of overtime this is. The multiplier comes from it.',
  payrollFrequency: 'How often this structure pays — monthly is the norm.',
  maxBenefits: 'The annual flexible benefit pot an employee on this structure may draw from.',
  formulaExample: 'Formula over the payroll variables, e.g. B * 0.25 for a quarter of base.',
  salaryStructureId: 'Which structure to assign.',
  baseOptional: 'The base to set for everyone selected. Leave empty to set it per employee afterwards.',
  amountBasedOnFormula: 'Compute the amount from a formula rather than using the fixed figure.',
  remark: 'Free note on the claim.',
  maxAmountPerClaim: 'The most one claim line of this type can be sanctioned for.',
  actualAmount: 'What was actually handed over, which may be less than the advance.',
  amountReturned: 'What the employee is handing back unspent.',
  clearanceDate: 'The date the payment cleared.',
  declarationDate: 'The date the declaration is made. It applies to the tax year chosen.',
  taxWithheldAtSource: 'Tax the other employer already deducted from that income.',
  applicationDate: 'The date the application is raised.',
  componentId: 'Which salary component this is claimed under.',
  claimDate: 'The date the claim is raised.',
  attachmentFileId: 'The evidence for this request — a receipt or a certificate, as a photo or PDF.',
  exemptionCategoryId: 'Which relief category this sits under.',
  jobTitle: 'The title shown on the careers page and the offer.',
  jobTitleAr: 'The Arabic title shown when the careers page is in Arabic.',
  salaryLowerRange: 'Bottom of the advertised range.',
  salaryUpperRange: 'Top of the advertised range.',
  publishSalaryRange: 'Shows the range on the careers page. Otherwise it is held internally.',
  publish: 'Puts the opening on the careers page immediately.',
  expectedByDate: 'When the position needs to be filled by.',
  referrerId: 'The member of staff who introduced the candidate.',
  appointmentLetterTemplateId: 'The template the letter’s body comes from.',
  appointmentDate: 'The date the appointment takes effect.',
  goalScoreWeightage: 'How much the goal score counts toward the final score. The three weights should total 100.',
  selfScoreWeightage: 'How much the self-assessment counts toward the final score.',
  feedbackScoreWeightage: 'How much peer feedback counts toward the final score.',
  programName: 'What the programme is called.',
  programNameAr: 'The Arabic name shown when the interface is in Arabic.',
  trainerEmail: 'Where the trainer is reached.',
  supplier: 'The external provider, if the programme is bought in.',
  feedbackText: 'What the participant thought of it.',
  licensePlate: 'The registration plate. Used as the vehicle’s name in lists.',
  make: 'Manufacturer.',
  modelYear: 'Year of manufacture.',
  fuelType: 'Petrol, diesel, or other. Fuel logs are checked against it.',
  lastOdometer: 'The reading now. Logs must not go backwards from it.',
  licenceExpiryDate: 'When the vehicle licence lapses. A lapsed one is a legal exposure.',
  insuranceCompany: 'Who insures the vehicle.',
  insuranceEndDate: 'When the vehicle insurance lapses.',
  carbonCheckDate: 'When the emissions certificate lapses.',
  vehicleId: 'Which vehicle this log is for.',
  odometer: 'The reading at the time of the log.',
  fuelQuantity: 'Litres put in, for consumption tracking.',
  totalCost: 'What the log cost. Posted to the vehicle’s running total on submit.',
  travelFunding: 'Who pays — the company, the employee, or a sponsor.',
  eventDetails: 'What the trip is for, in enough detail to approve it.',
  organizerAddress: 'Where the event or host is.',
  raisedByEmployeeId: 'Who is raising the case. Hidden from the record if raised anonymously.',
  againstParty: 'Who or what the grievance concerns.',
  isAnonymous: 'Withholds the complainant’s identity from everyone but the investigators.',
  providerId: 'Which insurer the policy is with.',
  providerName: 'The insurer’s name.',
  bypassLineManager: 'Routes the case past the line manager, for when the manager is its subject.',
  legalName: 'The name on the commercial register.',
  legalNameAr: 'The Arabic legal name, as registered.',
  taxId: 'The tax card number the company files under.',
  commercialRegister: 'The commercial register number.',
  taxOffice: 'The tax office the company reports to.',
  payrollCycle: 'Calendar month, or the 21st-to-20th cycle.',
  payrollCycleStartDay: 'The day of the month a payroll period starts on, when not calendar months.',
  minSalary: 'The lowest salary this grade permits.',
  maxSalary: 'The highest salary this grade permits.',
  isFixedTerm: 'Contracts of this type have an end date the system tracks.',
  isoCode: 'The two-letter ISO country code.',
  isLocal: 'Egyptian nationals. The foreign-worker ratio is measured against this.',
  requiresWorkPermit: 'Nationals of this country need a work permit, which is then a tracked document.',
  isDefault: 'Used where a record does not name one of its own.',
  doctype: 'Which kind of document this applies to.',
  event: 'What triggers it — a save, a submission, or a number of days either side of a date.',
  dateField: 'The date on the document the offset is measured from.',
  daysOffset: 'How many days before or after the date field to fire.',
  repeatEveryDays: 'Keeps firing at this interval until the item is resolved. Zero fires once.',
  recipientField: 'A field on the document naming who should be told.',
  recipientRoles: 'Roles whose holders should be told.',
  message: 'The body of the notification. Fields on the document can be referenced.',
  employeeNameAr: 'The Arabic full name, used on Arabic documents and payslips.',
  mealAllowanceMode: 'Whether this person gets the branch meal rate, a fixed amount, or nothing.',
  fixedMealAllowance: 'Overrides the branch daily meal rate for this person.',
  transportAllowanceMode: 'Whether this person gets the branch transport rate, a fixed amount, or nothing.',
  fixedTransportAllowance: 'Overrides the branch daily transport rate for this person.',
  variableMealAllowance: 'The month’s meal money at full attendance, paid by days attended ÷ scheduled working days (so it can exceed this figure); days with no attendance record, as for Always-Present staff, pay nothing.',
  variableNatureOfWorkAllowance: 'The month’s nature-of-work money at full attendance, paid by days attended ÷ scheduled working days (so it can exceed this figure); days with no attendance record, as for Always-Present staff, pay nothing.',
  bankName: 'The bank the salary is paid into.',
  bankAccountNo: 'The account the salary is paid into.',
  reviewerId: 'Who is giving the feedback.',
  cycleName: 'What the cycle is called.',
  kraName: 'What the key result area is called.',
  criteriaName: 'What the feedback criterion is called.',
  templateName: 'What the template is called.',
  typeName: 'What the type is called.',
  purposeName: 'What the purpose is called.',
  sourceName: 'Where candidates of this source come from.',
  termName: 'What the offer term is called.',
  interviewTypeName: 'What the interview type is called.',
  roundName: 'What the round is called.',
  expectedAverageRating: 'The panel average a candidate must reach to clear this round.',
  contactNumber: 'Contact number.',
  contractType: 'Fixed-term or open-ended.',
  contractNumber: 'Reference on the contract itself.',
  notes: 'Free notes.',
  qualification: 'The qualification held.',
  institution: 'Where it was obtained.',
  yearOfPassing: 'Year it was obtained.',
  specialization: 'The subject or field.',
  companyName: 'The employer.',
  designation: 'The role held there.',
  employerName: 'The employer the registration was under.',
  insuranceNumber: 'The insurance number under that employer.',
  questions: 'One question per line.',
  ruleName: 'What the rule is called.',
  interviewDate: 'When the interview takes place.',
  eventType: 'What kind of event — course, workshop, on-the-job.',
  encashmentDate: 'The date the encashment is booked on.',
  adjustmentDate: 'The date the adjustment is booked on.',
  managerId: 'Who requests from this branch go to when nobody nearer is named. Also one of its branch managers.',
  managerIds: 'Everyone who sets this branch’s weekly roster and sees its team in the app. They do not approve requests; the primary manager does.',
  resolutionDetails: 'What was decided, in the words that will stand as the record.',
  maritalStatus: 'For the record and for statutory forms that ask.',
  bloodGroup: 'For an emergency. Nothing else reads it.',
  photoFileId: 'A photo of the person, for badges and for the record.',
  personalEmail: 'Their own address. Used for a sign-in if no company one exists.',
  companyEmail: 'Their work address. Used for a sign-in created on hire.',
  currentAddress: 'Where they live now.',
  permanentAddress: 'Their permanent address, as on the national ID.',
  emergencyContactName: 'Who to call if something happens to them at work.',
  emergencyPhone: 'How to reach that person.',
  emergencyRelation: 'Who that person is to them.',
  reasonForLeaving: 'Why they left, in HR’s words. Kept once the status is Left.',
  exitFeedback: 'What they said on the way out, beyond the interview questionnaire.',
  reasonForResignation: 'Why they resigned, as they gave it at the exit interview.',
  isIndivisible: 'Half days are refused; the type is taken whole.',
  paidBySocialInsurance: 'Days of this type come off the employer’s payment days, because social insurance pays them, not the payslip.',
  allocateOnDay: 'For earned leave: whether the monthly slice is usable from the first day, held to the last, or dated to the joining anniversary.',
  stateAr: 'The state’s Arabic name, shown in an Arabic session.',
  // --- rest days, check-in and leave types ---
  weeklyOffDays: 'The rest days used for any week the weekly roster leaves empty. On an employee, empty falls back to the branch’s.',
  weeklyOffFrom: 'Days before this date keep the old pattern. Left empty, it applies to every date, closed months included.',
  punchAnywhere: 'No branch or geofence is asked of their phone punches — drivers, area managers.',
  allowedBranchIds: 'Other branches whose pin also counts for a phone check-in. Their own branch always counts.',
  attendanceDeviceId: 'The badge or enrolment number the terminal reports for this person.',
  allowNegative: 'For statutory leave nobody is allocated — Hajj, maternity, exams. Off, any days beyond the balance are taken unpaid and deducted from salary.',
  isCompensatory: 'Credited when someone works a weekly rest day or holiday — "Worked a day off" on the attendance screen.',
  isTerminal: 'A final state. Nothing moves a document out of it.',

  // --- data import and export ---
  matchField: 'The field that finds the existing record to update — usually its code.',
  readableLinks: 'Linked records as their code or name instead of ids.',
};

/**
 * Keyed by the button's visible label, lowercased. Matched exactly after
 * trimming, so "Submit" and "Submit slips" are separate entries.
 */
export const ACTION_HELP: Record<string, string> = {
  'generate for a range': 'Projects every active schedule over the dates chosen, past or future, skipping days already rostered. Written in bulk.',
  'accrue to date': 'Runs the monthly earned-leave accrual for every month from the date chosen up to the as-of date, for months the nightly job missed.',
  // --- lifecycle ---
  // `cancel` is deliberately absent: a dialog's Cancel button carries the same
  // label as the lifecycle action, and the lifecycle one explains itself in its
  // own confirmation.
  submit: 'Makes the document take effect. Most fields can no longer be edited afterwards.',
  amend: 'Creates a new draft that supersedes this cancelled document.',
  approve: 'Approves and submits, which is what applies the document.',
  'submit slips': 'Submits every slip in this run, making them payable.',

  // --- generic ---
  create: 'Adds a new record.',
  save: 'Stores what you have entered.',
  edit: 'Opens this record for changes.',
  delete: 'Removes this record permanently.',
  remove: 'Removes this record permanently.',
  add: 'Adds the row you have filled in above.',
  close: 'Closes without changing anything.',
  done: 'Closes this. Anything shown once will not be shown again.',
  clear: 'Empties the current selection.',
  new: 'Starts a new record.',
  xlsx: 'Downloads the current results as an Excel file.',
  csv: 'Downloads the current results as a CSV file.',
  run: 'Runs the report with the filters above.',

  // --- attendance and rostering ---
  'generate from checkins': 'Builds attendance from the punches in the range. Submitted records are never overwritten.',
  'mark a day': 'Marks a whole group for one day. People already marked are skipped.',
  'select unmarked': 'Selects everyone with no attendance yet on this date.',
  'assign a group': 'Rosters several people onto one shift in a single step.',
  'generate from schedules': 'Creates the next period’s assignments from the configured schedules.',
  'new request': 'Raises a request. It changes nothing until it is submitted.',
  replay: 'Reprocesses this punch now that its badge is known.',
  'map badge': 'Points this badge id at an employee, so its punches can be replayed.',
  'record a punch': 'Records a punch by hand, for one a terminal missed.',

  // --- devices ---
  'add device': 'Registers a terminal and issues the key its sync agent signs in with.',
  'issue key': 'Issues a new key. The current one stops working immediately.',
  disable: 'Stops this terminal being accepted at ingestion. It keeps its history.',
  enable: 'Allows this terminal to send punches again.',
  details: 'How this terminal is reached, how its last pulls went, and the router setup it needs.',
  'add agent': 'Names a machine that will read terminals over its own network, and issues the code its installer enrols with.',
  'download agent': 'The agent program for Windows, to install on the machine that reaches the terminals.',
  'rotate token': 'Gives the agent a new token on its next heartbeat. Nothing stops; the old token lapses after 15 minutes.',
  're-issue code': 'A fresh enrolment code for a rebuilt machine. The current token is revoked at once.',
  'revoke agent': 'Refuses this agent from now on. Its terminals go unread until it is re-enrolled or moved to Pull.',
  'move all to pull': 'Hands every terminal this agent reads back to this server’s schedule, which needs their port forwards open.',

  // --- payroll ---
  'run payroll': 'Builds a slip for everyone in scope for the chosen period.',
  preview: 'Shows who is in scope, and why anyone is excluded, before anything is created.',
  'retry failed': 'Re-runs only the employees whose slips failed.',
  'generate calendar months': 'Creates one period per calendar month for the year.',
  'generate 21st-to-20th': 'Creates periods on the 21st-to-20th cycle instead of calendar months.',
  'derive from attendance': 'Fills the period variables from what attendance recorded.',
  'derive from records': 'Fills this from the records already captured, rather than typing it.',

  // --- leave ---
  'apply for leave': 'Raises a leave application for approval.',
  allocate: 'Adds days to a balance. It moves nothing until submitted.',
  'run accrual': 'Accrues entitlement up to the date shown. Safe to re-run.',
  'expire carry-forward': 'Lapses carried-forward days that are past their deadline.',
  encash: 'Pays a balance out instead of taking it as time off.',
  'adjust a balance': 'Corrects a balance directly, where no allocation explains the movement.',

  // --- expenses ---
  sanction: 'Decides the claim line by line. What is sanctioned is what becomes payable.',
  'offset advance': 'Settles this claim against an advance the employee already holds.',
  'record payment': 'Marks the claim as paid and closes it.',
  pay: 'Records the money as handed over.',
  return: 'Records unspent money handed back.',
  lines: 'Opens the individual lines that make up this document.',
  'new claim': 'Raises an expense claim.',
  'new advance': 'Raises an advance, to be paid out before the spend.',

  // --- talent ---
  'create appraisal': 'Starts an appraisal for this person in the chosen cycle.',
  'apply template': 'Replaces the KRA rows with the template’s, along with their weightings.',
  'save ratings': 'Stores the KRA completion figures and recalculates the score.',
  'save self-assessment': 'Stores the employee’s own score and reflections.',
  recalculate: 'Recomputes from the current figures. Use after anything it depends on changes.',
  attendees: 'Sets who is expected at this event.',
  attendance: 'Records who actually came, which is what credits the skill.',
  questions: 'Sets the questions the leaver is sent. Fix these before sending.',
  send: 'Sends the questionnaire to the leaver.',
  accepted: 'Records that the candidate accepted. Only an accepted offer carries into onboarding.',
  rejected: 'Records that the candidate declined. The vacancy stays open.',
  'mark filled': 'Closes the requisition against the staffing plan, freeing the budgeted vacancy.',
  'send interview reminders': 'Emails everyone with an interview scheduled.',
  investigators: 'Grants these people the case narrative, which is withheld from everyone else.',

  // --- system ---
  'change password': 'Changes your own password.',
  'sign out everywhere else': 'Ends every other session but this one.',
  'set password': 'Sets a password for this user. They must change it at next sign-in.',
  validate: 'Runs the import without writing anything, to see what would be rejected.',
  import: 'Writes the file to the database. Validate first.',
  'download template': 'Downloads a spreadsheet with the columns this doctype expects.',
  'download rejected rows': 'Downloads the rejected rows with the reason for each, to fix and import again.',
  'import another file': 'Starts again with a new file for the same doctype.',
  'download setup workbook': 'Downloads the four-sheet workbook — Branches, Departments, Designations, Employees — to fill in and upload.',
  'import another workbook': 'Clears what was just imported from the screen and waits for the next workbook.',
  'choose columns': 'Picks which columns the export carries. With none picked, it carries them all.',
  attach: 'Attaches the chosen file to this record.',
  'mark read': 'Marks this notification as read.',
  'mark all read': 'Marks every notification as read.',
  calculate: 'Works out the figure from the values entered, without changing anything.',

  // --- openers named for what they create ---
  assign: 'Rosters everyone selected onto the chosen shift from the date given.',
  'assign to an employee': 'Puts one employee on this structure from an effective date.',
  'assign and submit': 'Assigns the policy to one employee and allocates it in the same step.',
  'new policy': 'Records an employer insurance policy for an employee.',
  'new type': 'Adds an overtime type — the multiplier ships as configuration, not code.',
  'new structure': 'Starts a salary structure. It does nothing until it is assigned.',
  components: 'Sets which earnings and deductions this structure draws on.',
  allocations: 'Shows what has been allocated to this employee from their benefit pot.',
  'edit changes': 'Opens the field changes this transfer or promotion will apply on submit.',
  'edit lines': 'Opens the individual lines that make up this claim.',
  'new application': 'Raises a request against the flexible benefit pot.',
  'new category': 'Adds a declarable relief with its two caps.',
  'new sub-category': 'Adds a heading under a relief category.',
  'new proof': 'Records evidence for a declared relief.',
  declare: 'Raises a tax exemption declaration for the year.',
  'declare income': 'Records income earned elsewhere, which selects the bracket without being taxed.',
  'new opening': 'Opens a vacancy against the staffing plan.',
  'new plan': 'Starts a staffing plan — the budget openings are checked against.',
  positions: 'Sets how many of each designation the plan budgets for.',
  unpublish: 'Takes the opening off the careers page. Applications already received are kept.',
  'new referral': 'Records a candidate introduced by a member of staff.',
  'new letter': 'Drafts an appointment letter for an accepted candidate.',
  'new applicant': 'Adds a candidate by hand, for one who did not apply through the careers page.',
  'schedule interview': 'Books a round for an applicant.',
  feedback: 'Opens what the panel recorded for this interview.',
  'new programme': 'Defines a training programme its events belong to.',
  'new result': 'Records the outcome of an event for its participants.',
  'new feedback': 'Records what a participant thought of an event.',
  'add vehicle': 'Registers a vehicle so logs and documents can be booked against it.',
  'add member': 'Puts one person on this rota on top of the branch’s own staff — cover from elsewhere.',
  itinerary: 'Sets the legs of the trip. The request dates come from here.',
  costings: 'Sets what the trip is expected to cost. Sponsorship nets off against it.',
  'raise a case': 'Opens a grievance. The narrative is withheld from everyone but the investigators.',
  "send today's requests": 'Asks everyone in the group what they worked on today. Skips holidays and approved leave.',
  reply: 'Records what you worked on, against the request sent to you.',
  'run renewal sweep': 'Flags every policy inside its renewal window and notifies the owner.',
  export: 'Downloads the current results.',
  annual: 'Calculates the year’s income tax from the annual figures entered.',
  monthly: 'Calculates one month’s withholding from the monthly figure entered.',
  'other deductions': 'Calculates the non-tax statutory deductions for the gross entered.',
  'issue new key': 'Issues a new key. The current one stops working immediately.',
  mark: 'Marks everyone selected with the chosen status for the day.',
  'worked a day off': 'Marks a rest day or holiday as worked and credits the day in lieu in the same step.',
  adjust: 'Corrects this person’s balance of one leave type directly.',

  // --- weekly roster ---
  'copy last week': 'Copies last week’s shifts and days off into this week’s empty days. Days already set are kept.',
  'fill from usual pattern': 'Fills the empty days from each person’s usual days off and default shift. Days already set are kept.',
  publish: 'Makes it live: an opening goes on the careers page; a roster week becomes what attendance and the app follow.',
  withdraw: 'Returns the published week to draft until it is published again.',
  'open in weekly roster': 'Opens the roster behind this week, at its branch, job and week, to change it.',

  // --- attendance review ---
  'review attendance': 'Opens the attendance review for this period.',
  'mark as verified': 'Signs the period’s attendance off. The run is never blocked without it, but its rosters can no longer change.',
  reopen: 'Withdraws the sign-off, so the period’s attendance and rosters can change again.',
  'decide…': 'Skips or confirms every undecided flag of the kinds you tick, for this employee.',
  'decide for this page…': 'Skips or confirms every undecided flag of the kinds you tick, for everyone on this page.',
  'skip…': 'Leaves this flag uncharged, with a reason and a file if you want them.',
  'confirm…': 'Charges this flag: shows the proposed penalty days, or the overtime hours and type, to change before saving.',
  'change…': 'Opens the confirmed figure to change it.',

  // --- system logs ---
  'more filters': 'Adds filters for the user, the record and the result.',
  'show changes': 'Lists each field this changed, with its value before and after.',
  'clear filters': 'Removes every filter, back to the latest entries.',
};

/** The label a button carries, normalised for lookup. */
export const helpForAction = (label: string): string | undefined =>
  ACTION_HELP[label.trim().toLowerCase().replace(/\s+/g, ' ')];

/**
 * Keyed by the section heading's text, lowercased. Only sections whose purpose
 * is not obvious from the heading carry one — an `i` on every heading is noise.
 */
export const SECTION_HELP: Record<string, string> = {
  'add a restriction': 'Narrows every list this user opens to rows linked to the record named here.',
  adjustments: 'A direct correction to a balance where no allocation or application explains the movement.',
  agents: 'Machines inside the branches that read terminals over the LAN and send the punches here. A silent agent takes every terminal behind it with it.',
  'agent setup': 'What a terminal needs before an agent can read it: its LAN address, its comm key, and an enrolled agent on the same network.',
  'allocate a policy to a group': 'Opens a leave year across a branch, department or grade in one step.',
  applications: 'Requests raised by employees. Approving one is what applies it.',
  'attendance and days': 'How the days on a payslip are counted before any money is worked out.',
  'appointment letters and referrals': 'The paperwork that follows an accepted offer, and candidates introduced by staff.',
  appraisals: 'One per person per cycle. The final score is derived from the weighted components, not typed.',
  approvers: 'Held per kind of request, so a leave approver is not automatically an expense approver.',
  assignments: 'A structure does nothing until it is assigned; the assignment carries the money.',
  'attendance requests': 'For days a terminal never saw. Submitting is what writes the attendance records.',
  'benefit pot and payouts': 'What the employee may draw on, and what has already been paid from it.',
  'biometric devices': 'The terminals attendance is built from. A silent one makes its branch wrong.',
  cycles: 'A cycle carries the weighting between goals, self-assessment and feedback.',
  'daily work summary': 'Holidays and anyone on approved leave are skipped; re-running asks nobody twice.',
  'department detail': 'Rename it, see what sits beneath it, and set who approves for it.',
  'documents expiring': 'An expired licence or emissions test is a legal exposure, not a reminder.',
  'employee advances': 'Money handed over before the spend, recovered against a claim or returned.',
  'employee record': 'Contracts, statutory documents, education and history for one person.',
  'exemption categories': 'The declarable relief and its two caps — an absolute one and a share of income.',
  'exit interviews': 'Sent to the leaver rather than filled in for them, so set the questions first.',
  'expense types': 'What a claim line can be booked against. A cap is checked at sanction, not at claim.',
  'gratuity rules': 'What the end-of-service preview is calculated against.',
  'grievance investigators': 'Assigning someone grants them the case narrative, which HR cannot see.',
  'hr settings': 'How far ahead an expiry becomes a warning. Read by the nightly sweep.',
  'health insurance': 'The employer’s own scheme, separate from statutory Universal Health Insurance.',
  'import history': 'Every run, including dry runs, with what each one rejected.',
  ledger: 'Every movement behind a balance, including the reversing entries a cancellation writes.',
  'mandatory documents': 'What each person must hold, and who is short of it.',
  offers: 'Only an accepted offer carries into onboarding.',
  'onboarding and separation': 'Checklists that apply to the employee record when submitted.',
  'organisation setup': 'One workbook for a whole company; problems are listed before anything is written.',
  'other income': 'Not taxed on the payslip, but selects a harsher bracket column.',
  'overtime requests': 'The multiplier comes from the overtime type, which ships as configuration.',
  'payroll runs': 'Per-employee failures are surfaced on the run rather than aborting it.',
  'peer feedback': 'Folds into the score when the reviewer submits it.',
  'period variable pay': 'Absences and penalties for one period, held apart from the salary assignment.',
  'policies expiring': 'A lapsed policy is a live exposure, not a reminder.',
  'proofs and declared other income': 'Evidence for a declared relief, and income earned elsewhere.',
  requisitions: 'The request that justifies an opening, checked against the staffing plan.',
  rounding: 'Whether the net pay on a slip is a whole amount or kept to the piastre.',
  'salary components': 'The earnings and deductions a structure draws on, fixed or by formula.',
  'skill gaps': 'Where someone falls short of what their own designation requires.',
  'skill map': 'What one person holds, against what their designation asks for.',
  'staffing plans': 'The budget an opening is checked against. No plan row means no budgeted vacancy.',
  'tax exemption declarations': 'Recalculate after income moves: the percentage cap follows taxable income.',
  'tax position': 'Declared relief against what is actually allowed once the caps are applied.',
  'tracked documents': 'The records behind the compliance counts, created and corrected here.',
  'transfers and promotions': 'The changed fields are the document; on submit they are applied.',
  'travel requests': 'Dates come from the itinerary and cost from the costings, so neither is typed twice.',
  'vehicle logs': 'Submitting posts the cost to the vehicle; cancelling reverses it.',
  schedules: 'The rota a branch runs. Everyone active at the branch is on it; a transfer moves them to the new branch’s rota on its effective date.',
  'what can move this document': 'Answered for you specifically — a document you raised will not offer its own approval.',
  'who is missing one': 'Names the people rather than counting them, so it can be worked through.',
  'attendance review': 'What confirming each review flag proposes, and whether a flag nobody decides is skipped or applied.',
  balances: 'Everyone at once: allocated, taken and remaining for each leave type.',
};

/** The heading text, normalised for lookup. */
export const helpForSection = (heading: string): string | undefined =>
  SECTION_HELP[heading.trim().toLowerCase().replace(/\s+/g, ' ')];


/**
 * Keyed by the column header as rendered. Columns that share a field's label
 * fall back to the field's copy in the guide, so only headers that mean
 * something of their own are listed here.
 */
export const COLUMN_HELP: Record<string, string> = {
  Pin: 'Opens the branch’s recorded location in a map. Empty means none has been set.',
  Step: 'One thing the company needs before it can run a payroll, in the order it is needed.',
  Reference: 'The document’s own number, from its naming series.',
  Status: 'Where this sits in its own lifecycle — separate from draft and submitted.',
  State: 'Draft, submitted or cancelled.',
  Document: 'Draft, submitted or cancelled.',
  Actions: 'What can be done to this row, given its state and your permissions.',
  Active: 'Whether this record is still offered for new documents.',
  Type: 'Which kind this is.',
  Kind: 'Which kind this is.',
  Date: 'The date the document is booked on.',
  When: 'When it happened.',
  From: 'First day covered.',
  To: 'Last day covered.',
  Days: 'How many days it covers.',
  Amount: 'The figure on the document.',
  Net: 'What is actually paid, after deductions.',
  Gross: 'Earnings before deductions.',
  Payable: 'What is left to pay after everything netted off.',
  'Employer cost': 'What the employee costs the company beyond gross pay. Reported, never deducted.',
  Doctype: 'Which kind of document.',
  ID: 'The system reference.',
  'Employee no.': 'Your own payroll number for this person.',
  'National ID': 'Egyptian national ID.',
  Joined: 'Start of service.',
  Insurance: 'Whether social insurance applies.',
  Valid: 'Documents in date.',
  'Expiring soon': 'Documents inside the warning window.',
  Expired: 'Documents past their expiry. A live exposure, not a reminder.',
  Missing: 'Mandatory documents with no record at all.',
  Number: 'The number printed on the document.',
  Holder: 'Who or what the document belongs to.',
  Scan: 'Whether the document itself is held.',
  Late: 'Arrived after the shift started.',
  'Late (min)': 'How late, in minutes.',
  'Early exit': 'Left before the shift ended.',
  'OT hrs': 'Hours beyond the shift, if the shift allows overtime.',
  Meal: 'Meal allowance earned for the day, from the branch rate.',
  Transport: 'Transport allowance earned for the day, from the branch rate.',
  Origin: 'Where the record came from — the auto pass, a request, leave, or by hand.',
  Hours: 'Working hours recorded.',
  Assignment: 'The assignment’s number.',
  Device: 'The terminal’s serial.',
  'Last seen': 'When the terminal last contacted the API.',
  'Last punch': 'The newest punch it has sent.',
  'Total punches': 'How many punches it has sent in all.',
  'Badge id': 'The enrolment number the terminal reported.',
  English: 'The wording as the interface asks for it — this is the key, so it is never edited here.',
  Translation: 'What Arabic shows instead. Saving takes effect for everyone, without a deployment.',
  Time: 'When the punch happened.',
  Reason: 'Why it was rejected, or why it was raised.',
  Allocations: 'Days allocated in this period.',
  Begins: 'When the checklist starts.',
  'Joining / relieving': 'The employee’s first or last day, whichever applies.',
  Effective: 'When the change takes effect.',
  Changes: 'Which fields the document changes.',
  Posted: 'The date it was booked.',
  Years: 'Years of service the gratuity is calculated on.',
  'Applicable earnings': 'The earnings the gratuity rule is applied to.',
  Receivable: 'What the employee is owed.',
  'Asset recovery': 'Company property not returned, netted off.',
  'Reason for leaving': 'What the leaver gave as the reason.',
  'Would rehire': 'Whether the interviewer would take them back.',
  Run: 'The payroll run’s number.',
  Employees: 'How many people are in scope.',
  Slips: 'How many slips were built.',
  'Absent days': 'Unpaid absences in the period.',
  Penalty: 'Deductions imposed in the period.',
  Slip: 'The slip’s number.',
  'Payment days': 'Days actually paid, after absences.',
  Deductions: 'Everything taken off gross.',
  Frequency: 'How often the structure pays.',
  Currency: 'The currency amounts are held in.',
  'Benefit pot': 'The annual flexible benefit allowance.',
  'Effective from': 'When the assignment starts applying.',
  Base: 'The salary the structure is built on.',
  Variable: 'The variable component of pay.',
  'Insurable wage': 'The wage social insurance is calculated on.',
  Taxable: 'Whether it counts toward taxable income.',
  Claimed: 'What was claimed.',
  Sanctioned: 'What was approved, line by line. This is what becomes payable.',
  'Advance offset': 'Advance money netted off against the claim.',
  Advance: 'What was advanced.',
  Instalment: 'How it is recovered from salary.',
  Paid: 'What was handed over.',
  Recovered: 'What payroll has taken back so far.',
  Outstanding: 'What the employee still holds.',
  'Tax year': 'The year it applies to.',
  Declared: 'What the employee declared.',
  'Allowed after caps': 'What survives the annual and percentage caps.',
  Submitted: 'Whether it has been submitted.',
  Evidenced: 'Whether proof has been provided.',
  Pot: 'The annual allowance.',
  Allocated: 'Drawn from the pot.',
  Used: 'Claimed against the allocation.',
  'Claimed on': 'The date of the claim.',
  Category: 'The relief category.',
  'Annual cap': 'The absolute annual ceiling.',
  '% of income': 'The cap as a share of net taxable income.',
  Title: 'The job title.',
  Positions: 'How many are being hired.',
  Vacancies: 'How many are still open.',
  'Careers page': 'Whether it is published.',
  Plan: 'The staffing plan.',
  'Budgeted positions': 'How many the plan allows for.',
  Designation: 'The job title.',
  Offered: 'When the offer was made.',
  Who: 'The person concerned.',
  Applicant: 'The candidate.',
  Scheduled: 'When the interview is booked for.',
  Average: 'The panel’s average rating.',
  Rating: 'Score given.',
  Round: 'Which interview round.',
  'Clears at': 'The average a candidate must reach to pass.',
  Goals: 'The goal component of the score.',
  Self: 'The self-assessment component.',
  Feedback: 'The peer feedback component.',
  Final: 'The weighted final score.',
  About: 'Who the feedback is about.',
  Reviewer: 'Who gave it.',
  Score: 'The score given.',
  Cycle: 'The appraisal cycle.',
  Goal: 'What is being measured.',
  'Progress %': 'How far along.',
  Programme: 'The training programme.',
  Trainer: 'Who delivers it.',
  Event: 'The training event.',
  Level: 'Who it is pitched at.',
  Vehicle: 'The vehicle.',
  Plate: 'The registration plate.',
  Fuel: 'Fuel type, or fuel put in.',
  Odometer: 'The reading.',
  Logs: 'How many logs are booked against it.',
  Distance: 'Distance covered.',
  Service: 'When service is next due.',
  'Cost per km': 'Running cost divided by distance.',
  Driver: 'Who drives it.',
  'Total cost': 'Running cost to date.',
  Cost: 'What it cost.',
  Departs: 'Departure date.',
  Returns: 'Return date.',
  Sponsored: 'What a sponsor pays.',
  'Company pays': 'What the company pays after sponsorship.',
  Purpose: 'What it is for.',
  'Raised by': 'Who raised it. Hidden when raised anonymously.',
  'Raised on': 'When it was raised.',
  Detail: 'The narrative — visible to investigators only.',
  Subject: 'The heading.',
  Group: 'The summary group.',
  Asked: 'How many were asked.',
  Replied: 'How many replied.',
  Digest: 'Whether the digest was sent.',
  Provider: 'The insurer.',
  Cover: 'What the policy covers.',
  'Annual premium': 'What it costs a year.',
  Policies: 'How many policies with this provider.',
  Dependants: 'How many dependants are covered.',
  'Annual cost': 'Total premium a year.',
  Ends: 'When it lapses.',
  Expires: 'When it lapses.',
  Policy: 'The policy.',
  Period: 'The period it belongs to.',
  Company: 'The legal entity.',
  'Tax card': 'The tax card number.',
  'Commercial reg.': 'The commercial register number.',
  'Social insurance': 'The establishment number.',
  'Payroll cycle': 'Calendar month or 21st-to-20th.',
  Code: 'The record’s code.',
  Abbr: 'The abbreviation.',
  Branch: 'The location.',
  'Biometric device': 'The terminal whose punches route here.',
  'Meal / day': 'Meal allowance per attended day.',
  'Transport / day': 'Transport allowance per attended day.',
  Description: 'Free notes.',
  'Min salary': 'The lowest this grade permits.',
  'Max salary': 'The highest this grade permits.',
  'Fixed term': 'Whether contracts of this type have an end date.',
  ISO: 'The two-letter country code.',
  Egyptian: 'Whether nationals count as local.',
  'Work permit': 'Whether nationals need a work permit.',
  File: 'The file imported.',
  Mode: 'Insert, update or upsert.',
  Rows: 'Rows accepted of rows read.',
  Rejected: 'Rows rejected, with reasons.',
  Rule: 'The rule.',
  'Fires on': 'What triggers it.',
  Offset: 'Days before or after the date.',
  Repeat: 'How often it repeats until resolved.',
  Recipients: 'Who is told.',
  Enabled: 'Whether it fires at all.',
  Key: 'The string’s key.',
  Namespace: 'Which part of the interface it belongs to.',
  Value: 'The wording shown.',
  Chain: 'The approval chain.',
  States: 'The states it moves through.',
  Mobile: 'Mobile number.',
  Language: 'Interface language.',
  Roles: 'What the user may do.',
  'Last sign-in': 'When they last signed in.',
  Role: 'The role.',
  Label: 'How it appears on screen.',
  'Arabic label': 'How it appears in Arabic.',
  System: 'Whether it is built in and cannot be removed.',
  'Desk access': 'Whether the role may sign in to the back office.',
  Disabled: 'Whether the role is switched off.',
  Job: 'The scheduled job.',
  Processed: 'How many items the last run handled.',
  Duration: 'How long the last run took.',
  'Last run': 'When it last ran.',
  Message: 'What the last run reported.',
  Employee: 'Who this row is about.',
  Name: 'What the record is called.',
  'Arabic name': 'The Arabic name.',
  Component: 'The salary component.',
  Source: 'Where it came from.',
  Location: 'Where it is.',
  Email: 'Email address.',
  Total: 'The total.',
  'Device id': 'The terminal serial.',
  'Cost to company': 'Gross pay plus employer contributions. Level-1: absent for anyone without the grant.',
  In: 'First punch of the day, as the terminal saw it.',
  Out: 'Last punch of the day.',
  Holiday: 'The day was a holiday on the employee’s list.',
  'Shift window': 'The shift the punch was matched to.',
  'Balance then': 'The balance at the moment the application was made.',
  Started: 'When the run began.',
  Completed: 'When the run finished, or blank while it is still going.',
  Measured: 'Hours attendance actually recorded — what an approver compares the claim against.',
  Rate: 'The hourly rate the overtime was priced at, fixed at approval.',
  Derived: 'When the figures were derived from attendance; by hand means someone typed them.',
  Overtime: 'Approved overtime paid on this slip.',
  'Gross YTD': 'Gross pay so far this tax year, this slip included.',
  'Net YTD': 'Net pay so far this tax year, this slip included.',
  Reimbursed: 'What has actually been paid out against the claim.',
  'Paid on': 'The date the payment was recorded.',
  Budget: 'What the plan’s positions are expected to cost in total.',
  'Requested by': 'Who raised the requisition.',
  'Days to fill': 'From the requisition to the day it was marked filled.',
  Resolved: 'The date the case was closed.',
  'Radius (m)': 'How far from the pin a phone check-in still counts as being at work.',
  Department: 'The department the person is posted to.',
  'Punches (30d)': 'Punches in the last 30 days that matched no shift, so built no attendance.',
  'What is missing': 'The one field that would give this person an approver.',
  'Counts as': 'What attendance made of the punch — in, out, ignored or not counted. The terminal’s own flag is on hover.',
  Pay: 'Paid, partly unpaid or unpaid: days past the balance are deducted from salary.',
  Attachment: 'The evidence attached to the request. Click to download it.',
  Field: 'Which field changed.',
  Before: 'The value before the change; — means it was empty.',
  After: 'The value after the change.',
  Was: 'The value before the transfer or promotion.',
  Became: 'The value it applied.',
  Change: 'The transfer or promotion that made the change.',
  'Unpaid leave': 'Leave days taken unpaid in the period, deducted from salary.',
  Absent: 'Absent days charged in the period.',
  'Early (min)': 'How early they left, in minutes.',
  'Overtime hours': 'Hours beyond the shift that day.',
  'Net payable': 'What the settlement pays after everything netted off.',
  'Relieving date': 'The employee’s last day. Separation activities are dated back from it.',
  'Years of service': 'Years of service the gratuity is calculated on.',
  Returned: 'What the employee is handing back unspent.',
};

/** The header text, matched exactly. */
export const helpForColumn = (header: string): string | undefined =>
  COLUMN_HELP[header.trim()] ?? FIELD_HELP[header.trim()];
